<template>
  <div class="market">
    <div class="market__header">
      <div>
        <h2 class="market__title">{{ $t('influencer_panel.market') }}</h2>
        <p class="market__subtitle">{{ $t('influencer_panel.market_hint') }}</p>
      </div>
      <b-badge variant="light-secondary">{{ $t('influencer_panel.market_freshness') }}</b-badge>
    </div>

    <div class="market__grid">
      <!-- Posicionamento só aparece quando há base de comparação suficiente; o backend
           devolve percentil nulo quando não há, e aí o bloco inteiro sai. -->
      <hb-section
        v-if="positioning && positioning.percentile"
        :title="$t('influencer_panel.positioning')"
        :subtitle="$t('influencer_panel.positioning_hint')"
      >
        <div class="market__rank">
          <span class="market__rank-icon"><feather-icon icon="AwardIcon" size="20" /></span>
          <div>
            <div class="market__rank-value">
              {{ $t('influencer_panel.top_percent', { pct: positioning.percentile }) }}
            </div>
            <div class="market__rank-hint">
              {{ $t('influencer_panel.among_peers', { count: positioning.peers }) }}
            </div>
          </div>
        </div>

        <div class="market__benchmarks">
          <benchmark-bar
            :label="$t('influencer_panel.conversion_rate')"
            :mine="positioning.mine.conversion_rate"
            :average="positioning.platform.conversion_rate"
            suffix="%"
          />
          <benchmark-bar
            :label="$t('influencer_panel.avg_ticket')"
            :mine="positioning.mine.avg_ticket_cents"
            :average="positioning.platform.avg_ticket_cents"
            money
          />
          <benchmark-bar
            :label="$t('influencer_panel.commission')"
            :mine="positioning.mine.commission_cents"
            :average="positioning.platform.avg_commission_cents"
            money
          />
        </div>
      </hb-section>

      <hb-section
        v-if="hasTiming"
        :title="$t('influencer_panel.timing')"
        :subtitle="$t('influencer_panel.timing_hint')"
      >
        <div class="market__block-label">{{ $t('influencer_panel.weekday') }}</div>
        <div class="market__bars">
          <div v-for="day in timing.weekday" :key="`d${day.bucket}`" class="market__bar-col">
            <span class="market__bar-pct">{{ day.pct }}%</span>
            <div class="market__bar-track">
              <div class="market__bar-value" :style="{ height: `${heightOf(day, timing.weekday)}%` }" />
            </div>
            <span class="market__bar-label">{{ weekdayLabel(day.bucket) }}</span>
          </div>
        </div>

        <div class="market__block-label market__block-label--spaced">{{ $t('influencer_panel.hour_range') }}</div>
        <div class="market__bars">
          <div v-for="slot in timing.hour" :key="`h${slot.bucket}`" class="market__bar-col">
            <span class="market__bar-pct">{{ slot.pct }}%</span>
            <div class="market__bar-track">
              <div class="market__bar-value" :style="{ height: `${heightOf(slot, timing.hour)}%` }" />
            </div>
            <span class="market__bar-label">{{ hourLabel(slot.bucket) }}</span>
          </div>
        </div>

        <p class="market__note">
          <feather-icon icon="ClockIcon" size="13" />
          {{ $t('influencer_panel.timing_note') }}
        </p>
      </hb-section>
    </div>

    <hb-section
      v-if="opportunities.length"
      :title="$t('influencer_panel.opportunities')"
      :subtitle="$t('influencer_panel.opportunities_hint')"
    >
      <template #actions>
        <router-link :to="{ name: 'influencer-panel-campaigns', query: { tab: 'disponiveis' } }" class="market__all">
          {{ $t('influencer_panel.see_all') }}
          <feather-icon icon="ChevronRightIcon" size="14" />
        </router-link>
      </template>

      <div v-for="item in opportunities" :key="item.id" class="market__opportunity">
        <div class="market__opportunity-identity">
          <div class="market__opportunity-name">
            {{ item.name }}
            <b-badge v-if="item.recent_sales > 0" variant="light-success">
              {{ $t('influencer_panel.recent_sales', { count: item.recent_sales }) }}
            </b-badge>
          </div>
          <div class="market__opportunity-entity">{{ item.legal_entity }}</div>
        </div>
        <div class="market__opportunity-stat">
          <div class="market__opportunity-label">{{ $t('influencer_panel.commission') }}</div>
          <div class="market__opportunity-value">{{ commissionOf(item) }}</div>
        </div>
      </div>
    </hb-section>
  </div>
</template>

<script>
import { BBadge } from 'bootstrap-vue'
import HbSection from '@/components/hubee/HbSection.vue'
import BenchmarkBar from './BenchmarkBar.vue'
import { campaignCommissionLabel } from '../campaign'

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb']

/**
 * "Mercado Hubee": onde o influencer esta em relacao aos pares, quando o publico dele
 * compra e que campanhas estao vendendo.
 *
 * O que o design pedia e nao esta aqui: **sazonalidade da modalidade** e **crescimento
 * mes a mes**. Os dois precisam de historico, e o modulo inteiro nasceu em agosto de
 * 2026 — desenhar uma curva anual com um mes de dado seria inventar tendencia.
 */
export default {
  name: 'MarketSection',
  components: { BBadge, HbSection, BenchmarkBar },
  props: {
    positioning: { type: Object, default: null },
    timing: { type: Object, default: null },
    opportunities: { type: Array, default: () => [] },
  },
  computed: {
    hasTiming() {
      return !!this.timing && (this.timing.weekday || []).some(day => day.count > 0)
    },
  },
  methods: {
    weekdayLabel(bucket) {
      return WEEKDAYS[Number(bucket)] || ''
    },
    /** Faixas de 3 horas: bucket 0 = 00-03, bucket 6 = 18-21. */
    hourLabel(bucket) {
      const start = Number(bucket) * 3

      return `${String(start).padStart(2, '0')}-${String(start + 3).padStart(2, '0')}`
    },
    heightOf(item, rows) {
      const max = Math.max(...rows.map(row => Number(row.pct || 0)), 1)

      return Math.max(4, Math.round((Number(item.pct || 0) / max) * 100))
    },
    commissionOf(item) {
      return campaignCommissionLabel(item)
    },
  },
}
</script>

<style scoped>
.market {
  display: flex;
  flex-direction: column;
  gap: clamp(1rem, 1.8vw, 1.5rem);
}

.market__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding-top: 0.5rem;
}

.market__title {
  margin: 0;
  font-size: clamp(1.286rem, 2vw, 1.714rem);
  font-weight: var(--fw-medium);
  color: var(--text-heading);
}

.market__subtitle {
  margin: 0.2rem 0 0;
  font-size: var(--fs-sm);
  color: var(--text-muted);
}

.market__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: clamp(1rem, 1.8vw, 1.5rem);
}

.market__rank {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  margin-bottom: 1.25rem;
}

.market__rank-icon {
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-pill);
  background: var(--tint-warning);
  color: var(--warning);
}

.market__rank-value {
  font-size: var(--fs-h2);
  font-weight: var(--fw-medium);
  color: var(--text-heading);
  line-height: 1.2;
}

.market__rank-hint {
  font-size: var(--fs-sm);
  color: var(--text-muted);
}

.market__benchmarks {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.market__block-label {
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  color: var(--text-heading);
  margin-bottom: 0.75rem;
}

.market__block-label--spaced {
  margin-top: 1.5rem;
}

.market__bars {
  display: flex;
  align-items: flex-end;
  gap: 0.35rem;
}

.market__bar-col {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
}

.market__bar-pct {
  font-size: var(--fs-xs);
  color: var(--text-muted);
}

.market__bar-track {
  width: 100%;
  height: 70px;
  display: flex;
  align-items: flex-end;
  background: var(--surface-sunken);
  border-radius: var(--radius-sm);
  overflow: hidden;
}

.market__bar-value {
  width: 100%;
  background: var(--primary);
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
}

.market__bar-label {
  font-size: var(--fs-xs);
  color: var(--text-muted);
  white-space: nowrap;
}

.market__note {
  margin: 1.1rem 0 0;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: var(--fs-xs);
  color: var(--text-muted);
}

.market__all {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.9rem;
  font-weight: var(--fw-medium);
}

.market__opportunity {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  padding: 0.85rem 0.5rem;
  border-bottom: 1px solid var(--border-color);
}

.market__opportunity:last-of-type {
  border-bottom: none;
}

.market__opportunity-identity {
  flex: 1 1 220px;
  min-width: 0;
}

.market__opportunity-name {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: var(--fw-medium);
  color: var(--text-heading);
}

.market__opportunity-entity {
  font-size: var(--fs-sm);
  color: var(--text-muted);
}

.market__opportunity-stat {
  flex: 0 0 auto;
  margin-left: auto;
  text-align: right;
}

.market__opportunity-label {
  font-size: var(--fs-xs);
  color: var(--text-muted);
}

.market__opportunity-value {
  font-weight: var(--fw-medium);
  color: var(--text-heading);
}
</style>
